import React, { useState } from 'react';
import {
    AppBar, Toolbar, Typography, Box, Avatar, Menu, MenuItem,
    IconButton, Drawer, List, ListItemIcon, ListItemText,
    useMediaQuery, useTheme, Divider, ListItemButton, Chip, Alert, Container, Skeleton
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PeopleIcon from '@mui/icons-material/People';
import CodeIcon from '@mui/icons-material/Code';
import TerminalIcon from '@mui/icons-material/Terminal';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import PublicIcon from '@mui/icons-material/Public';
import NotificationCenter from './NotificationCenter';
import PVLogo from './PVLogo';
import { LanguageMenuButton } from './LanguageSelector';
import { useTranslation } from 'react-i18next';

import ScienceIcon from '@mui/icons-material/Science';

const DRAWER_WIDTH = 280;

const AdminLayout = ({ children }) => {
    const { t } = useTranslation();
    const { logout, currentUser } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [anchorEl, setAnchorEl] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const adminMenuItems = [
        { text: t('Overview'), icon: <DashboardIcon />, path: '/admin' },
        { text: t('Demo Generator'), icon: <ScienceIcon />, path: '/admin/demo-data' },
        { text: t('User Management'), icon: <PeopleIcon />, path: '/admin/quota' },
        { text: t('Symbol Sync'), icon: <PublicIcon />, path: '/admin/securities' },
        { text: t('Admin Scripts'), icon: <CodeIcon />, path: '/admin/scripts' },
        { text: t('Server Logs'), icon: <TerminalIcon />, path: '/admin/logs' },
        { text: t('Data Import'), icon: <CloudUploadIcon />, path: '/admin/import' },
        { text: t('Data Export'), icon: <CloudDownloadIcon />, path: '/admin/export' },
    ];

    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setDrawerOpen(open);
    };

    const handleLogout = () => {
        handleMenuClose();
        logout();
    };

    const isItemActive = (itemPath) => {
        if (itemPath === '/admin') {
            return location.pathname === '/admin';
        }
        if (itemPath === '/admin/demo-data') {
            return location.pathname === '/admin/demo-data' || location.pathname === '/admin-demo';
        }
        if (itemPath === '/admin/quota') {
            return location.pathname === '/admin/quota' || location.pathname === '/admin-quota';
        }
        if (itemPath === '/admin/scripts') {
            return location.pathname === '/admin/scripts' || location.pathname === '/admin-scripts';
        }
        if (itemPath === '/admin/logs') {
            return location.pathname === '/admin/logs' || location.pathname === '/admin-logs';
        }
        if (itemPath === '/admin/import') {
            return location.pathname === '/admin/import' || location.pathname === '/import';
        }
        if (itemPath === '/admin/export') {
            return location.pathname === '/admin/export' || location.pathname === '/export';
        }
        return location.pathname === itemPath;
    };

    const drawer = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'surface-container-low' }}>
            {/* Admin Header Branding */}
            <Box sx={{ p: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PVLogo sx={{ width: 36, height: 36 }} />
                <Box>
                    <Typography variant="h6" noWrap sx={{ fontWeight: 800, color: 'primary.main', letterSpacing: '-0.03em', lineHeight: 1.2 }}>
                        {t('PV Admin')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AdminPanelSettingsIcon sx={{ fontSize: 14, color: 'secondary.main' }} /> {t('Control Console')}
                    </Typography>
                </Box>
            </Box>

            <Divider sx={{ my: 1, opacity: 0.5 }} />

            {/* Admin Navigation List */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, pt: 1 }}>
                <Typography variant="caption" sx={{ px: 2, py: 1, color: 'text.secondary', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block' }}>
                    {t('Management Modules')}
                </Typography>
                <List sx={{ pt: 0 }}>
                    {adminMenuItems.map((item) => {
                        const active = isItemActive(item.path);
                        return (
                            <ListItemButton
                                key={item.text}
                                selected={active}
                                onClick={() => {
                                    navigate(item.path);
                                    if (isMobile) setDrawerOpen(false);
                                }}
                                sx={{
                                    borderRadius: 2,
                                    mb: 1,
                                    bgcolor: active ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                                    color: active ? 'primary.main' : 'text.primary',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 40, color: active ? 'primary.main' : 'text.secondary' }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.text}
                                    primaryTypographyProps={{ fontWeight: active ? 700 : 600, fontSize: '0.95rem' }}
                                />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Box>

            <Divider sx={{ mx: 2, my: 1, opacity: 0.5 }} />

            {/* Switch Back to Main User View */}
            <Box sx={{ p: 2 }}>
                <ListItemButton
                    onClick={() => navigate('/')}
                    sx={{
                        borderRadius: 2,
                        mb: 1,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.2s',
                        '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: alpha(theme.palette.primary.main, 0.04)
                        }
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: 'primary.main' }}>
                        <ExitToAppIcon />
                    </ListItemIcon>
                    <ListItemText
                        primary={t('Exit to Main App')}
                        secondary={t('Return to user books')}
                        primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9rem', color: 'primary.main' }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                    />
                </ListItemButton>

                <ListItemButton
                    onClick={handleLogout}
                    sx={{ borderRadius: 2, color: 'error.main' }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('Logout')} primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItemButton>
            </Box>
        </Box>
    );

    if (currentUser && !currentUser.is_super_admin) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8 }}>
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {t('Super Admin access is required to view the Admin Console.')}
                </Alert>
            </Container>
        );
    }

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'surface' }}>
            {/* Admin Sidebar Drawer */}
            <Box
                component="nav"
                sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
            >
                <Drawer
                    variant={isMobile ? 'temporary' : 'permanent'}
                    open={isMobile ? drawerOpen : true}
                    onClose={toggleDrawer(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: DRAWER_WIDTH,
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'surface-container-low'
                        },
                    }}
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* Admin Main Content Area */}
            <Box component="main" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', width: { md: `calc(100% - ${DRAWER_WIDTH}px)` } }}>
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        top: 0,
                        zIndex: (theme) => theme.zIndex.appBar,
                        bgcolor: (theme) => alpha(theme.palette.background.default, 0.85),
                        backdropFilter: 'blur(20px)',
                        color: 'text.primary',
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Toolbar>
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={toggleDrawer(!drawerOpen)}
                            sx={{
                                mr: 2,
                                color: 'primary.main',
                                display: { md: 'none' }
                            }}
                        >
                            <MenuIcon />
                        </IconButton>

                        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography
                                variant="h6"
                                noWrap
                                sx={{
                                    fontWeight: 700,
                                    color: 'primary.main',
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                {t('Admin Console')}
                            </Typography>
                            <Chip
                                label={t('SUPER ADMIN')}
                                size="small"
                                color="secondary"
                                sx={{ fontWeight: 800, fontSize: '0.7rem', height: 22 }}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                            <LanguageMenuButton />
                            {currentUser && (
                                <>
                                    <NotificationCenter />
                                    {currentUser.first_name || (!currentUser.username?.includes('@') ? currentUser.username : null) ? (
                                        <>
                                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>
                                                {currentUser.first_name || currentUser.username}
                                            </Typography>
                                            <IconButton onClick={handleMenuOpen} size="small" sx={{ p: 0.5 }} aria-label="Account settings">
                                                <Avatar sx={{ width: 36, height: 36, bgcolor: 'secondary.main', fontSize: '1rem', fontWeight: 700 }}>
                                                    {(currentUser.first_name || currentUser.username).charAt(0).toUpperCase()}
                                                </Avatar>
                                            </IconButton>
                                        </>
                                    ) : (
                                        <>
                                            <Skeleton variant="text" width={60} height={20} sx={{ display: { xs: 'none', sm: 'block' } }} />
                                            <IconButton onClick={handleMenuOpen} size="small" sx={{ p: 0.5 }} aria-label="Account settings">
                                                <Skeleton variant="circular" width={36} height={36} />
                                            </IconButton>
                                        </>
                                    )}
                                </>
                            )}
                        </Box>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleMenuClose}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    overflow: 'visible',
                                    filter: 'drop-shadow(0px 12px 32px rgba(28, 28, 24, 0.08))',
                                    mt: 1.5,
                                    width: 200,
                                    borderRadius: 2
                                },
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
                                <PersonIcon sx={{ mr: 2, fontSize: 20, color: 'text.secondary' }} /> {t('Profile')}
                            </MenuItem>
                            <MenuItem onClick={() => { handleMenuClose(); navigate('/'); }}>
                                <ExitToAppIcon sx={{ mr: 2, fontSize: 20, color: 'primary.main' }} /> {t('Exit to Main App')}
                            </MenuItem>
                            <Divider sx={{ my: 1, opacity: 0.5 }} />
                            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                                <LogoutIcon sx={{ mr: 2, fontSize: 20 }} /> {t('Logout')}
                            </MenuItem>
                        </Menu>
                    </Toolbar>
                </AppBar>

                {/* Page Content */}
                <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
};

export default AdminLayout;
